import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RotateCcw, Loader2 } from 'lucide-react';
import { NativeFilterSelect } from './NativeFilterSelect';
import { AsyncModelSearchSelect } from './AsyncModelSearchSelect';
import { CodiceOemAutocomplete } from './CodiceOemAutocomplete';
import { useProductFacets, useOrigine, Origine, ORIGINE_PREDEFINITA_LABEL } from '../../hooks/useProductFacets';

interface Props {
  className?: string;
  variant?: 'default' | 'flat';
}

/**
 * Widget compatto "Ricerca Avanzata". Marche/origini sono caricate via
 * `/api/products/facets`. Le "Tipologie" mostrano i GRUPPI merceologici
 * (30 valori aggregati, campo `gruppi` della risposta) invece dei
 * sottogruppi granulari (55 valori, campo `categories`) usati dal filtro
 * Tipologia del catalogo — stessa cascata per marca, solo lista più
 * leggibile per una ricerca rapida da homepage (richiesta utente
 * 13/07/2026). Il selettore modello è asincrono, ricerca server-side su
 * `/api/abbinatore/autocomplete` (indici Mongo, trigger dalla 2ª lettera) —
 * sostituisce il vecchio dump statico di ~5k modelli da `compatibility`.
 */
export function RicercaAvanzataWidget({ className = '', variant = 'default' }: Props) {
  const navigate = useNavigate();
  const [marca, setMarca] = useState('');
  const [tipologia, setTipologia] = useState('');
  const [modelloId, setModelloId] = useState('');
  const [modelloLabel, setModelloLabel] = useState('');
  const [codice, setCodice] = useState('');

  // Stessa cascata (e stesso azzeramento della tipologia incoerente) della
  // barra filtri del catalogo: una sola implementazione per entrambe.
  const { facets, loadingFacets, origineOptions, originiSelezione } =
    useProductFacets(marca, tipologia, { onTipologiaNonPiuValida: () => setTipologia('') });
  // Senza originali nella selezione passa da solo a "Solo compatibili".
  const { origine, scegliOrigine } = useOrigine(originiSelezione);

  const isDirty = !!(marca || tipologia || origine || modelloId || codice);

  const reset = () => {
    setMarca('');
    setTipologia('');
    scegliOrigine('');
    setModelloId('');
    setModelloLabel('');
    setCodice('');
  };

  const submit = (e?: React.FormEvent, codiceOverride?: string) => {
    if (e) e.preventDefault();
    const effectiveCodice = codiceOverride ?? codice;

    // Se è stato selezionato un modello, la ricerca avanzata porta
    // direttamente alla pagina "Consumabili per modello" con quel
    // modello preselezionato (match esatto per compatibilità), invece
    // di una ricerca testuale generica sul catalogo.
    if (modelloId) {
      navigate(`/consumabili-per/${modelloId}`);
      return;
    }

    const dirty = !!(marca || tipologia || origine || modelloId || effectiveCodice);
    if (!dirty) {
      navigate('/prodotti/tutti');
      return;
    }

    const params = new URLSearchParams();
    if (marca) params.set('marca', marca);
    if (origine) params.set('origine', origine);
    if (effectiveCodice.trim()) params.set('q', effectiveCodice.trim());

    const categoryParam = tipologia || 'tutti';
    const queryString = params.toString();
    navigate(`/prodotti/${categoryParam}${queryString ? `?${queryString}` : ''}`);
  };

  // Quando cambia marca, azzera il modello selezionato (filtro non
  // più coerente).
  const handleMarcaChange = (next: string) => {
    setMarca(next);
    setModelloId('');
    setModelloLabel('');
  };

  const wrapperClass = variant === 'flat'
    ? `rounded-xl border bg-white p-5 ${className}`
    : `rounded-xl border bg-white shadow-lg flex flex-col ${className}`;

  return (
    <form
      onSubmit={submit}
      className={wrapperClass}
      data-testid="ricerca-avanzata-widget"
    >
      {/* Intestazione con accento blu — rounded-t-xl (non overflow-hidden sul
          contenitore) per non troncare i dropdown dei filtri sottostanti. */}
      <div className={variant === 'flat' ? 'mb-4' : 'bg-[hsl(214,58%,41%)] rounded-t-xl px-5 pt-4 pb-3 mb-0'}>
        <h2 className={`text-base font-semibold tracking-tight flex items-center gap-2.5 ${variant === 'flat' ? '' : 'text-white'}`}>
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0 ${variant === 'flat' ? 'bg-[hsl(214,58%,91%)] text-[hsl(214,58%,41%)]' : 'bg-white/20 text-white'}`}>
            <Search className="w-4 h-4" />
          </span>
          Ricerca avanzata
          {loadingFacets && <Loader2 className={`w-3.5 h-3.5 animate-spin ml-auto ${variant === 'flat' ? 'text-muted-foreground' : 'text-white/70'}`} />}
        </h2>
      </div>

      <div className={`space-y-2 ${variant === 'flat' ? '' : 'px-5 pt-3'}`}>
        {/* Marche */}
        <NativeFilterSelect
          value={marca}
          onChange={handleMarcaChange}
          disabled={loadingFacets}
          testid="ra-select-marca"
          placeholder="Tutte le marche"
          options={facets.brands.map(b => ({ value: b, label: b }))}
        />

        {/* Tipologie — GRUPPI merceologici (30 valori aggregati, campo
            `gruppi`), non i sottogruppi granulari del filtro catalogo
            (richiesta utente 13/07/2026). */}
        <NativeFilterSelect
          value={tipologia}
          onChange={setTipologia}
          disabled={loadingFacets}
          testid="ra-select-tipologia"
          placeholder="Tutte le tipologie"
          options={facets.gruppi.map(c => ({ value: c.slug, label: c.label }))}
        />

        {/* Origine — "Originali/Compatibili" (tutto) o "Solo compatibili" */}
        <NativeFilterSelect
          value={origine}
          onChange={v => scegliOrigine(v as Origine)}
          testid="ra-select-origine"
          placeholder={ORIGINE_PREDEFINITA_LABEL}
          options={origineOptions}
        />

        {/* Modello — ricerca async sul DB abbinatore_modelli */}
        <AsyncModelSearchSelect
          value={modelloId}
          valueLabel={modelloLabel}
          onChange={(id, label) => { setModelloId(id); setModelloLabel(label); }}
          marca={marca || undefined}
          testid="ra-select-modello"
          placeholder="MODELLI"
          searchPlaceholder={marca ? `Cerca modello ${marca}...` : 'Cerca modello (min. 2 caratteri)...'}
          emptyMessage={marca ? `Nessun modello ${marca}` : 'Nessun modello trovato'}
        />

        {/* Cod. DTK/OEM — con autocompletamento leggero (solo codici, richiesta utente 24/02/2026). Selezionare un suggerimento avvia subito la ricerca (richiesta utente 24/02/2026, seguito). */}
        <CodiceOemAutocomplete
          value={codice}
          onChange={setCodice}
          onSelect={code => submit(undefined, code)}
          placeholder="Cod. DTK/OEM"
          testid="ra-input-codice"
        />
      </div>

      {/* Spacer: assorbe l'eventuale altezza extra quando il banner a
          destra è naturalmente più alto del widget, cosi le due colonne
          restano sempre alla stessa altezza (richiesta utente
          24/02/2026: banner allineato al box di ricerca). */}
      <div className="flex-1" />

      {/* Azioni */}
      <div className={`mt-3 flex items-center justify-end gap-2 ${variant === 'flat' ? '' : 'px-5 pb-4'}`}>
        <button
          type="button"
          onClick={reset}
          disabled={!isDirty}
          className="h-10 px-4 rounded-lg border bg-white text-sm font-medium hover:bg-secondary/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          data-testid="ra-btn-reset"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Azzera filtri
        </button>
        <button
          type="submit"
          className="h-10 px-5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-semibold hover:bg-[hsl(214,58%,46%)] transition-colors inline-flex items-center gap-1.5"
          data-testid="ra-btn-search"
        >
          Cerca
        </button>
      </div>
    </form>
  );
}
